import { FC, useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import { candidaturaService } from '@/services/candidaturaService'
import { CaptchaWidget, captchaObrigatorio } from '@/components/common/CaptchaWidget'

/**
 * "Quero ser um Consultor" — página pública (PDF do Consultor, Etapa 1).
 *
 * Coleta o mínimo para a Originei decidir se quer conversar. O cadastro completo
 * (formação, histórico, dados bancários e fiscais) só vem depois da aprovação, por
 * convite — pedir tudo aqui seria coletar dado pessoal de quem talvez nem siga adiante.
 */
const QueroSerConsultorPage: FC = () => {
  const navigate = useNavigate()

  const [nome, setNome] = useState('')
  const [email, setEmail] = useState('')
  const [telefone, setTelefone] = useState('')
  const [resumo, setResumo] = useState('')
  const [captchaToken, setCaptchaToken] = useState('')
  const [enviando, setEnviando] = useState(false)
  const [enviado, setEnviado] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  // useCallback: sem isso a identidade da função muda a cada render e o efeito do
  // widget o remontaria a cada tecla digitada, descartando o token já obtido.
  const receberToken = useCallback((token: string) => setCaptchaToken(token), [])

  const emailValido = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())
  const podeEnviar =
    nome.trim().length >= 3 &&
    emailValido &&
    telefone.trim().length >= 8 &&
    (!captchaObrigatorio() || !!captchaToken)

  const enviar = async () => {
    if (!podeEnviar) return
    setEnviando(true)
    setErro(null)
    try {
      await candidaturaService.enviar({
        nome: nome.trim(),
        email: email.trim(),
        telefone: telefone.trim(),
        resumo: resumo.trim() || undefined,
        captchaToken: captchaToken || undefined,
      })
      setEnviado(true)
    } catch (e) {
      setErro((e as { message?: string })?.message ?? 'Não foi possível enviar agora. Tente novamente.')
      // Token do Turnstile é de uso único: mantê-lo faria a próxima tentativa
      // falhar por um motivo diferente do que o usuário acabou de ver.
      setCaptchaToken('')
    } finally {
      setEnviando(false)
    }
  }

  if (enviado) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
        <Card sx={{ maxWidth: 520, width: '100%' }}>
          <CardContent sx={{ textAlign: 'center', py: 5 }}>
            <CheckCircleOutlineIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h5" fontWeight={700} gutterBottom>
              Candidatura enviada
            </Typography>
            {/* Deliberadamente não diz se o e-mail já estava na fila: isso
                transformaria a página numa sonda para descobrir quem se candidatou. */}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              A Originei vai analisar seu perfil e entrar em contato para uma apresentação e
              entrevista. O cadastro completo é liberado depois dessa etapa.
            </Typography>
            <Button variant="outlined" onClick={() => navigate('/login')}>
              Voltar ao início
            </Button>
          </CardContent>
        </Card>
      </Box>
    )
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
      <Card sx={{ maxWidth: 560, width: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            Quero ser um Consultor
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Conte um pouco sobre você. Se o perfil fizer sentido, a Originei entra em contato
            para uma apresentação e entrevista — e só então o cadastro completo é liberado.
          </Typography>

          <Divider sx={{ mb: 3 }} />

          {erro && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {erro}
            </Alert>
          )}

          <Stack spacing={2}>
            <TextField
              label="Nome completo"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              fullWidth
              size="small"
              required
            />
            <TextField
              label="E-mail"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={!!email && !emailValido}
              helperText={!!email && !emailValido ? 'E-mail inválido' : ' '}
              fullWidth
              size="small"
              required
            />
            <TextField
              label="Telefone / WhatsApp"
              value={telefone}
              onChange={(e) => setTelefone(e.target.value)}
              fullWidth
              size="small"
              required
            />
            <TextField
              label="Sobre sua experiência"
              value={resumo}
              onChange={(e) => setResumo(e.target.value)}
              placeholder="Em quais segmentos atua, de qual mercado vem, que produtos financeiros já comercializou"
              multiline
              minRows={4}
              fullWidth
              size="small"
            />

            <CaptchaWidget onToken={receberToken} />

            <Button
              variant="contained"
              size="large"
              onClick={enviar}
              disabled={!podeEnviar || enviando}
              startIcon={enviando ? <CircularProgress size={18} color="inherit" /> : undefined}
            >
              {enviando ? 'Enviando…' : 'Enviar candidatura'}
            </Button>

            <Button variant="text" size="small" onClick={() => navigate('/login')}>
              Já tenho acesso
            </Button>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  )
}

export default QueroSerConsultorPage
